import * as React from "react";

/**
 * ENH-376 / design.md D18 — the fill-and-track primitive extracted from
 * `components/ProgressBar` and shared with `components/LoyaltyProgressBar`.
 * Deliberately dumb: no domain knowledge of points, tiers or rewards. The
 * caller computes `proportion` (0-1) using whatever arithmetic its own
 * domain needs and hands over a plain number — this component only draws
 * it. Do not add points/tier-shaped props here; that logic belongs in each
 * caller (see `LoyaltyProgressBar`'s `resolveFilledProportion`).
 *
 * `trackClassNames`/`fillClassNames` exist so a caller can layer its own
 * CSS-based defaults on top (`LoyaltyProgressBar` passes its own
 * `LoyaltyProgressBar-track`/`-fill` class names through here to keep its
 * existing stylesheet — default colors, the narrow-width media query,
 * REQ-LPB-060 — applying unchanged; this component's own base class names
 * carry no CSS of their own today). Structural positioning (the track is
 * the fill's positioning context; the fill is absolutely placed inside it)
 * is baked in here as inline styles rather than left to each caller's CSS,
 * since it's required for correct rendering, not a stylistic default.
 */
export interface ProgressBarFillProps {
  displayType: "linear" | "circular";
  /** 0-1. Not clamped here — callers clamp using their own domain rules. */
  proportion: number;
  fillColor: string;
  trackColor: string;
  classNames?: string;
  trackClassNames?: string;
  fillClassNames?: string;
  /** Linear mode only. */
  width?: string | number;
  /**
   * Dwightbot round 2: deliberately no default here, unlike `width`.
   * `LoyaltyProgressBar` passes `get(style, "height")`, undefined when a
   * config hasn't set one, and relies on that undefined reaching the DOM as
   * no inline height at all so `LoyaltyProgressBar.css`'s own
   * `.LoyaltyProgressBar-track { height: 16px }` fallback applies. A local
   * default here would silently outrank that CSS. `ProgressBar` (the other
   * caller) is unaffected either way — it supplies its own explicit default
   * before ever calling this.
   *
   * Vivek, PR 584 review: that fallback is narrower in practice than this
   * comment used to claim. It only applies to a config with no height at
   * all, and every seeded widget has one (REQ-LPS-006 makes `style.height`
   * the merchant's bar-height control), so in practice the inline value
   * wins. The narrow-width track-height override this comment used to cite
   * was dead for exactly that reason and has been removed.
   */
  height?: string | number;
  borderRadius?: string | number;
  /** Circular mode only. */
  strokeWidth?: number;
  radius?: number;
  /**
   * Rendered after the fill, inside the track — e.g. a marker image riding
   * the fill's leading edge (`LoyaltyProgressBar`'s only use of this).
   * Linear mode only; the caller positions it (this component doesn't know
   * marker semantics).
   */
  overlay?: React.ReactNode;
}

export const ProgressBarFill: React.FC<ProgressBarFillProps> = ({
  displayType,
  proportion,
  fillColor,
  trackColor,
  classNames,
  trackClassNames,
  fillClassNames,
  width = "500px",
  height,
  borderRadius,
  strokeWidth = 0,
  radius = 0,
  overlay,
}) => {
  const percent = proportion * 100;

  if (displayType === "circular") {
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    return (
      <svg height={radius * 2} width={radius * 2} className={classNames}>
        <circle
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={fillColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          style={{
            strokeDasharray: `${circumference} ${circumference}`,
            strokeDashoffset: -(circumference - (percent / 100) * circumference),
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
            transition: "stroke-dashoffset 0.35s",
          }}
        />
      </svg>
    );
  }

  return (
    <div
      className={`ProgressBarFill-track ${trackClassNames || ""} ${
        classNames || ""
      }`}
      style={{
        width,
        height,
        backgroundColor: trackColor,
        borderRadius,
        position: "relative",
      }}
    >
      <div
        className={`ProgressBarFill-fill ${fillClassNames || ""}`}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: `${percent}%`,
          height,
          backgroundColor: fillColor,
          borderRadius,
        }}
      />
      {overlay}
    </div>
  );
};
